// User types matching the backend (snake_case from Rust serde serialization)
// Hierarchy: SuperAdmin(5) > Owner(4) > Admin(3) > Manager(2) > BoardMember(1)
export enum UserType {
  SUPER_ADMIN = 'super_admin',
  OWNER = 'owner',
  ADMIN = 'admin',
  MANAGER = 'manager',
  BOARD_MEMBER = 'board_member',
}

// Helper to check if user has admin-level or higher access
export const hasAdminAccess = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Helper to check if user has manager-level or higher access
export const hasManagerAccess = (userType: string | null): boolean => {
  if (!userType) return false;
  return [
    UserType.SUPER_ADMIN,
    UserType.OWNER,
    UserType.ADMIN,
    UserType.MANAGER,
  ].includes(userType as UserType);
};

// Bank connections hold a company's money-moving credentials, so managing them
// (create/rotate/update/delete) is restricted to SuperAdmin and the company's
// own Owner. Admin is deliberately excluded (may view accounts, not manage
// connections) — mirrors the backend `BankAuthorization::can_manage_credentials`.
export const canManageBankConnections = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER].includes(userType as UserType);
};

// Setting a vendor's bank details for ACH payables — the account a payment
// will send money to. Owner and Admin of the company only, and deliberately
// NOT super admins: this is the first bank write written under the rule that
// company roles own anything that moves money (root CLAUDE.md, rail 5).
// Mirrors the backend `BankAuthorization::can_manage_payees`.
export const canManagePayees = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.OWNER, UserType.ADMIN].includes(userType as UserType);
};

// Settling or cancelling ACH payables — the one action that moves money,
// done with the person's own one-time RSA code — and submitting a vendor
// payment. Owner and Admin of the company only, and NOT super admins (root
// CLAUDE.md, rail 5). Mirrors the backend `can_settle_payables` /
// `can_pay_vendor`.
export const canSettlePayables = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.OWNER, UserType.ADMIN].includes(userType as UserType);
};

// Submitting a vendor payment by ACH — token and add at the bank, which then
// holds it pending until someone settles. Owner and Admin of the company only,
// NOT super admins. Mirrors the backend `can_pay_vendor`; kept apart from
// `canSettlePayables` so the two can be tightened separately.
export const canPayVendor = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.OWNER, UserType.ADMIN].includes(userType as UserType);
};

// Viewing a company's bank accounts (live balances, read-only) is allowed for
// SuperAdmin, Owner, and Admin — mirrors the backend
// `BankAuthorization::can_view_accounts`. Admin can view accounts but not manage
// connections (see [[canManageBankConnections]]).
export const canViewBankAccounts = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Initiating a bank transfer (money movement) is allowed for SuperAdmin, Owner,
// and Admin — mirrors the backend `BankAuthorization::can_initiate_transfer`.
// Kept as a distinct helper (rather than reusing hasAdminAccess) so the
// money-movement bar can be tightened later without affecting other admin gates.
// Viewing transfer history is Manager+ (uses hasManagerAccess).
export const canInitiateTransfer = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Recording, voiding or resending an issued check — which posts to the GL and
// tells the bank a check is legitimate — is allowed for SuperAdmin, Owner, and
// Admin. Mirrors the backend `BankAuthorization::can_record_check`. Viewing the
// register is Manager+ (HOA-scoped), like transfer history.
export const canRecordCheck = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Placing, placing again or releasing a stop payment — a bank write that
// refuses a check — is allowed for a company's Owner and Admin, and NOT super
// admins. Mirrors the backend `can_manage_stop_payments`, which follows the
// payables checks (no super-admin arm). Viewing the list is HOA access.
export const canManageStopPayments = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.OWNER, UserType.ADMIN].includes(userType as UserType);
};

// Submitting a payer/lockbox file (an internal roster push to the bank — not
// money movement) and viewing its submission history is allowed for SuperAdmin,
// Owner, and Admin — mirrors the backend
// `BankAuthorization::can_submit_payer_file`. Managers/BoardMembers are excluded
// on the backend for the history reads too, so this gate covers both the submit
// button and the list itself.
export const canSubmitPayerFile = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// ---------------------------------------------------------------------
// Billing (Dues & Fees)
// ---------------------------------------------------------------------
//
// These mirror `back-end-rs/src/api/v1/billing/authorization.rs` one for
// one, and they deliberately do **not** collapse into hasManagerAccess /
// hasAdminAccess. The split between "billing a home" and "taking money
// off the books" is the whole shape of the module: a Manager does the
// operational work, an Owner/Admin does anything that reduces what an
// association is owed or that cannot be undone quietly. The backend
// returns 403 if the UI guesses, so a wrong gate here is a button that
// looks live and fails.
//
// BoardMember is read-only throughout: render the data, hide the button.
// The full table is `back-end-rs/docs/AUTHORIZATION_MATRIX.md`.

const isOwnerAdmin = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

const isManagerOrAbove = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Read the ledger, a balance, aging, a statement, a coupon, the policy,
// a run, a case, the review queue. Everyone with access to the HOA —
// BoardMembers included (`can_view_ledger`).
export const canViewLedger = (userType: string | null): boolean =>
  hasManagerAccess(userType) || userType === UserType.BOARD_MEMBER;

// Post a charge by hand (`can_post_charge`). Manager+: billing a home
// for something it owes is ordinary work, visible and reversible.
export const canPostCharge = isManagerOrAbove;

// Post a credit, waiver, write-off, or refund an overpayment
// (`can_post_credit`). Owner/Admin: these reduce what the association is
// owed and, unlike a charge, there is no bill anyone will query.
export const canPostCredit = isOwnerAdmin;

// Reverse a ledger entry, reverse a run, or return a payment as NSF
// (`can_reverse_entry`). Owner/Admin, same reasoning as a credit.
export const canReverseEntry = isOwnerAdmin;

// Change billing policy or notice rules, at HOA or company scope
// (`can_manage_policy`). Owner/Admin.
export const canManageBillingPolicy = isOwnerAdmin;

// Create and edit dues schedules and per-home overrides
// (`can_manage_schedules`). Manager+: nothing reaches the ledger until a
// run is posted, which is a separate Owner/Admin act.
export const canManageSchedules = isManagerOrAbove;

// Generate a billing run (`can_generate_runs`). Manager+ — generating
// produces a reviewable preview and charges nobody.
export const canGenerateRuns = isManagerOrAbove;

// Post a run to the ledger (`can_post_run`). Owner/Admin: the moment an
// association's members are actually billed.
export const canPostRun = isOwnerAdmin;

// Record a payment, or match one out of suspense (`can_record_payment`).
// Manager+. Returning or refunding one is canReverseEntry/canPostCredit.
export const canRecordPayment = isManagerOrAbove;

// Open or close an ownership tenure, add an account member, set the HOLD
// override (`can_manage_tenures`). Owner/Admin: a turnover retires an
// account number the bank and the homeowner both key on, and decides
// what happens to an unpaid balance.
//
// Adding a member is in here too, as of 2026-08-23. It used to be gated
// Manager+ on the backend — the one tenure write that did not match its
// neighbours — and was tightened once somebody had to decide: adding an
// owner changes who appears on the payer file and whose money the
// lockbox will accept, which is a money decision like the rest of them.
export const canManageTenures = isOwnerAdmin;

// Decide whether an association's BOARD MEMBERS may open a read-only view
// of a homeowner's portal (`can_set_board_impersonation`). Owner/Admin.
//
// Not Manager, and emphatically not BoardMember: the board is the party
// this governs, so letting a board member flip it would be the toggle
// deciding itself. Managers are excluded for a softer reason — this is a
// standing policy decision about who may read homeowners' financial
// history, not day-to-day association work.
export const canSetBoardImpersonation = isOwnerAdmin;

// Set or clear an association's explicit timezone (`can_set_timezone`,
// #599). Owner/Admin, and Managers — the server further limits a Manager
// to the HOAs they are assigned to. Not the board.
export const canSetHoaTimezone = hasManagerAccess;

// Run the aging, late-fee, interest, notice or balance-verify jobs by
// hand (`can_run_aging`, `can_verify_balances`). Owner/Admin: these
// write charges to every home in an association at once.
export const canRunBillingJobs = isOwnerAdmin;

// Advance a collections case (`can_advance_case`). Owner/Admin — demand,
// attorney, lien and foreclosure carry legal consequences and a person
// decides them.
export const canAdvanceCase = isOwnerAdmin;

// Open a collections case, or add a note to one (`can_work_case`).
// Manager+, and the split from canAdvanceCase is the point: noticing
// that a home needs working, and recording what was said on the phone,
// are day-to-day work. A case opens at `reminder`, exactly where the
// aging job would start it, so nothing here escalates anything.
export const canWorkCase = isManagerOrAbove;

// Issue an estoppel certificate and mark it delivered
// (`can_issue_estoppel`). Manager+: the figures come from the ledger
// rather than from anybody's typing, so the risk is in the data, which
// this cannot change.
export const canIssueEstoppel = isManagerOrAbove;

// Resend a member's WorkOS invitation (`can_resend_setup`, which
// delegates to `can_update_member`). Manager+, scoped to their own
// company server-side — a BoardMember's access to members is read-only.
//
// Worth knowing before changing this: since sign-in moved to AuthKit,
// resending the invitation is the **only** repair for a homeowner who
// cannot get in. There is no password to reset and no setup link to
// reissue; both were removed 2026-09-12.
export const canResendInvitation = isManagerOrAbove;

// Deactivate or reactivate a member's sign-in (`can_deactivate_member`).
// Manager+, scoped to their own company server-side; a BoardMember's
// access to members is read-only.
export const canDeactivateMember = isManagerOrAbove;

// Open a read-only view of a homeowner's portal (`can_impersonate`).
//
// **Shown to BoardMembers too, unlike most controls here**, because the
// answer is not a role question: it depends on `hoas.board_member_impersonation`,
// a per-HOA switch the management company sets, which this app cannot see
// without asking. Hiding the button for every board member would hide it
// in the associations that deliberately turned it on.
//
// So the control is offered and the server decides — it answers 403 with
// `IMPERSONATION_NOT_PERMITTED`, and the page says so. That is the right
// trade for a feature whose whole gate is data rather than role, and it
// costs a board member at a switched-off association one refusal.
//
// Everything that matters is enforced server-side regardless: the caller
// must reach **every** HOA the homeowner has a home in, the session is
// read-only by HTTP verb, and both the grant and the refusal are audited.
export const canImpersonateMember = (userType: string | null): boolean =>
  hasManagerAccess(userType) || userType === UserType.BOARD_MEMBER;

// Correcting a colleague's email address. Delegated to SuperAdmin, Owner
// and Admin — mirrors the backend's `can_update_user`, which additionally
// keeps Owners and Admins inside their own company and refuses a target
// that outranks them. Both of those are enforced server-side; this helper
// only decides whether the control is worth showing.
//
// Safe to delegate because it does not lower the bar to an account: the
// corrected address lands at WorkOS **unverified** and still has to be
// proved by whoever receives mail there before it can sign in.
export const canCorrectUserEmail = (userType: string | null): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Clearing someone's MFA factors. **SuperAdmin only**, and deliberately
// not the same set as [[canCorrectUserEmail]] — the two were weighed
// separately rather than shipped as one "user admin" permission.
//
// Resetting MFA *lowers* the bar to an account, which makes it the thing
// an attacker holding a compromised Owner session would reach for first.
// Mirrors the backend `clear_mfa_factors`, which checks the row.
export const canResetMfa = (userType: string | null): boolean => {
  return userType === UserType.SUPER_ADMIN;
};

// The internal roadmap (internal-portal, at /internal/roadmap). The API
// answers 403 to everyone else; the issue titles are internal.
export const canViewRoadmap = (userType: string | null): boolean => {
  return userType === UserType.SUPER_ADMIN;
};

// Whether the user-management screen is worth showing at all. Managers
// and BoardMembers manage nobody — the backend answers 403 to their list
// request — so they do not get the nav entry either.
export const canManageUsers = (userType: string | null): boolean => {
  return canCorrectUserEmail(userType) || canResetMfa(userType);
};

// ---------------------------------------------------------------------
// Violation comment threads
// ---------------------------------------------------------------------
//
// A violation carries two threads, split by audience: `Internal` is the
// management company talking among itself about an enforcement action,
// `Shared` adds the association's board members and the cited homeowner.
//
// The board sits on the homeowner's side of that line **on purpose**.
// They are the association's own volunteers, usually neighbours of the
// person being cited, and a manager is often managing the board's
// expectations about an action as much as the homeowner's. This removes
// read access board members had before the split shipped; that is the
// decision, not a side effect of it. Reasoning and the rejected
// three-tier alternative:
// `knowledge/notes/decision-violation-threads-exclude-the-board.md`.
//
// These mirror `visibility_scope_for` in
// `back-end-rs/src/api/v1/comment/authorization.rs`. Unlike the billing
// block above, "BoardMember is read-only" does **not** hold here: a board
// member may post to the shared thread. What they may not do is reach the
// internal one.

// Read the thread at all. Everyone with access to the HOA, BoardMembers
// included — but a BoardMember's server-side scope is `SharedOnly`, so
// "the thread" means a shorter list for them than for a Manager.
export const canViewViolationComments = (userType: string | null): boolean =>
  hasManagerAccess(userType) || userType === UserType.BOARD_MEMBER;

// Post a comment at all. The same set: a board member answering a
// homeowner is ordinary work. Which thread they may post to is the
// separate gate below.
export const canPostViolationComment = canViewViolationComments;

// Use the internal thread — post to it, see its tab, see the visibility
// badges on comments. Manager+.
//
// This gate is load-bearing beyond hiding a button. The backend answers a
// BoardMember posting `internal` with a **403 whose body is plain text**,
// not the JSON envelope the error helpers parse, so a request that gets
// through surfaces as a bare fallback string with nothing explaining it.
// The UI's job is to make the request unconstructable.
export const canPostInternalComment = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Attach or remove files on the violation itself. Manager+: the
// violation's attachments are management's evidence for the citation. A
// homeowner's photo belongs on their own comment, where it gets an author
// and a timestamp for free.
export const canManageViolationAttachments = (
  userType: string | null
): boolean => hasManagerAccess(userType);

// Moderate somebody else's comment — soft-delete it, or toggle approval.
// Owner/Admin, the same bar the billing block uses for anything that
// removes a record another person created.
//
// Note what is deliberately absent: a gate for editing *your own*
// comment. That is identity, not role, and putting it here would invite a
// caller to pass an admin check and get a 403 — management may not edit a
// homeowner's words at all, in a record the homeowner can read.
export const canModerateViolationComments = (
  userType: string | null
): boolean => {
  if (!userType) return false;
  return [UserType.SUPER_ADMIN, UserType.OWNER, UserType.ADMIN].includes(
    userType as UserType
  );
};

// Change a violation's own fields — status, severity, dates — which the
// Linear-style detail page (#603) edits in place through pickers. Mirrors
// `can_update_violation` in back-end-rs/src/api/v1/violation/authorization.rs:
// SuperAdmin, Owner, Admin and Manager; BoardMember is read-only. (A Manager
// must also be assigned to the violation's HOA, which the server checks and
// the UI cannot know; a refusal there surfaces as a save error.)
export const canEditViolation = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Create a violation — the sidebar's quick-create (#603 step 3) and the
// New Violation button. Mirrors `can_create_violation`: SuperAdmin, Owner,
// Admin and Manager; BoardMember may not. Same set as canEditViolation
// today, kept separate because the backend keeps them separate.
export const canCreateViolation = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Edit a work order — the detail page's pickers and its edit dialog.
// Mirrors `can_update_work_order`: SuperAdmin, Owner, Admin and Manager;
// BoardMember is refused. (A Manager must also be assigned to the work
// order's HOA, which only the server knows; a refusal surfaces as a save
// error.)
export const canEditWorkOrder = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Create a work order (#632). Mirrors `can_create_work_order`: SuperAdmin,
// Owner, Admin and Manager (a Manager only in an assigned HOA, which the
// server checks); BoardMember is read-only.
export const canCreateWorkOrder = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Send a message to homeowners (#632) — one member or a whole HOA.
// Mirrors `can_send_member_message`, which gates both: SuperAdmin, Owner,
// Admin and Manager; "Board Members cannot send messages to members".
export const canSendMemberMessage = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Create an association (#665). Mirrors `can_create_hoa`: SuperAdmin in any
// company, Owner and Admin in their own; Manager and BoardMember are refused.
export const canCreateHoa = (userType: string | null): boolean =>
  hasAdminAccess(userType);

// Create a vendor (#665). Mirrors `can_create_vendor`: SuperAdmin, Owner,
// Admin and Manager (a Manager only in an assigned HOA, which the server
// checks); "Board Members have read-only access".
export const canCreateVendor = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// Work-order comments (#616) run on the same two threads as a violation's,
// behind the same server-side `visibility_scope_for`: a BoardMember reads
// and posts to the shared thread and never sees the internal one. What
// differs is who "shared" reaches — the board only; a work order has no
// homeowner party. So the violation gates carry over unchanged:
// `canPostInternalComment` and `canModerateViolationComments` apply as-is.
export const canViewWorkOrderComments = canViewViolationComments;
export const canPostWorkOrderComment = canPostViolationComment;

// Attach files to a work-order comment. Mirrors the backend, which gates a
// comment attachment on `can_update_comment`: Manager+, BoardMember refused
// even on their own comment.
export const canAttachToWorkOrderComment = (userType: string | null): boolean =>
  hasManagerAccess(userType);

// ---------------------------------------------------------------------
// General ledger (the association's own books)
// ---------------------------------------------------------------------
//
// Mirrors the General Ledger block of
// `back-end-rs/docs/AUTHORIZATION_MATRIX.md`. Reading is `canViewLedger`
// above — the same gate billing reads with, because a BoardMember may
// see the chart, the periods, every report and the reconciliation list.
// What a board member may never do is the two things the month-end
// workflow exists for: matching, and closing.
//
// Three things here write to the books and they are NOT gated alike.
// The split has a reason, and it is the reason rather than the rank
// that must survive a refactor.

// Open, match, unmatch and complete a reconciliation (`can_reconcile`).
// Manager+: this is the month-end work itself. It writes no journal
// entry at all — a reconciliation records which of our postings the
// institution has seen, and the bank feed is evidence, not a posting
// source.
export const canReconcile = isManagerOrAbove;

// Post bank interest or a service charge off the statement, and read
// the two endpoints that reach the institution live (the suggested
// balance and the statement candidates).
//
// Manager+, and it DOES write a journal entry — the one place writing
// from the feed is correct, because double-counting needs two sources
// and interest has only the bank. Gated with reconciling rather than
// with the chart because it is routine work by the person already doing
// the reconciling: an Owner in the loop for a $4.12 interest credit
// means it stops being done at all.
export const canPostBankActivity = isManagerOrAbove;

// Edit the chart of accounts, set budgets, close or lock a period, and
// mark an investment account to market.
//
// Owner/Admin. Marking a reserve to market is a judgement about what
// the reserve is worth, not routine work; closing a period stops
// anything posting into it; and the chart is what every emitter
// resolves a role against.
export const canManageChart = isOwnerAdmin;

// ---------------------------------------------------------------------
// Amenity reservations (#640)
// ---------------------------------------------------------------------
//
// Mirrors `back-end-rs/src/api/v1/reservation/authorization.rs`: every
// staff role with access to the HOA may read amenities and bookings —
// the board included, read-only — while configuring amenities and
// deciding bookings is Manager+. The HOA-wide reservation policy (the
// good-standing gate above all) is Owner/Admin: whether an association
// suspends amenity use for delinquency is a legal choice that varies by
// state, not routine work.

// See the amenities, the calendar, the queue and a booking's history.
export const canViewReservations = (userType: string | null): boolean =>
  hasManagerAccess(userType) || userType === UserType.BOARD_MEMBER;

// Approve, deny, cancel, and book on a household's behalf (with
// per-rule overrides). Manager+; the board is read-only here as
// everywhere else.
export const canManageReservations = isManagerOrAbove;

// Create and configure amenities: details, hours, limits, fees,
// agreements, closures, photos, archive.
export const canManageAmenities = isManagerOrAbove;

// A reservation's two comment threads (#640 phase 2) follow the
// violation's rules: management reads and writes both, the board reads
// and writes the shared one and never sees the internal one. Attaching a
// file is Manager+, as on a work order.
export const canViewReservationComments = canViewViolationComments;
export const canPostReservationComment = canPostViolationComment;
export const canAttachToReservationComment = (
  userType: string | null
): boolean => hasManagerAccess(userType);

// Change the association's reservation policy: the good-standing gate
// and its thresholds, pending expiry, reminders, week start.
export const canManageReservationPolicy = isOwnerAdmin;
